import { useState } from 'react';
import { NavLink, Route, Routes } from 'react-router-dom';
import { BookOpen, CreditCard, FilePlus2, FileText, FlaskConical, KeyRound, LayoutDashboard, LogOut, Menu, X } from 'lucide-react';
import { lazy, Suspense } from 'react';
import ClientDashboard from './pages/client/ClientDashboard';
import ClientDocuments from './pages/client/ClientDocuments';
import NewDocumentPage from './pages/client/NewDocumentPage';
import KeysPage from './pages/client/KeysPage';
import BillingPage from './pages/client/BillingPage';
import IntegrationPage from './pages/client/IntegrationPage';
import { clearApiKey } from './services/api';

// Page de tests (manipulations directes) : absente du build de production
const TestPage = import.meta.env.DEV ? lazy(() => import('./pages/TestPage')) : null;

const navigation = [
  { name: 'Tableau de bord', to: '/', icon: LayoutDashboard, end: true },
  { name: 'Nouveau document', to: '/documents/new', icon: FilePlus2 },
  { name: 'Documents', to: '/documents', icon: FileText, end: true },
  { name: "Clés d'API", to: '/keys', icon: KeyRound },
  { name: 'Facturation', to: '/billing', icon: CreditCard },
  { name: 'Intégration', to: '/integration', icon: BookOpen },
  ...(TestPage ? [{ name: 'Tests', to: '/test', icon: FlaskConical, end: false }] : []),
];

function ClientApp({ onLogout }: { onLogout: () => void }) {
  const [open, setOpen] = useState(false);

  const logout = () => {
    clearApiKey();
    onLogout();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-slate-900 text-slate-300 transition-transform lg:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-16 items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" alt="" className="h-9 w-9" />
            <div>
              <p className="text-base font-semibold leading-tight text-white">Juriseal</p>
              <p className="text-xs text-slate-400">Espace client</p>
            </div>
          </div>
          <button className="text-slate-400 lg:hidden" onClick={() => setOpen(false)} aria-label="Fermer le menu">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {navigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? 'bg-indigo-600 text-white' : 'hover:bg-slate-800 hover:text-white'}`
              }
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </NavLink>
          ))}
        </nav>
        <button onClick={logout} className="m-3 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-slate-800 hover:text-white">
          <LogOut className="h-5 w-5" />
          Déconnexion
        </button>
      </aside>

      <header className="sticky top-0 z-30 flex h-14 items-center border-b border-slate-200 bg-white px-4 lg:hidden">
        <button onClick={() => setOpen(true)} aria-label="Ouvrir le menu">
          <Menu className="h-6 w-6 text-slate-700" />
        </button>
        <img src="/favicon.svg" alt="" className="ml-3 h-7 w-7" /><span className="ml-2 font-semibold text-slate-900">Juriseal · Espace client</span>
      </header>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl p-4 sm:p-8">
          <Routes>
            <Route path="/" element={<ClientDashboard />} />
            <Route path="/documents" element={<ClientDocuments />} />
            <Route path="/documents/new" element={<NewDocumentPage />} />
            <Route path="/keys" element={<KeysPage />} />
            <Route path="/billing" element={<BillingPage />} />
            <Route path="/integration" element={<IntegrationPage />} />
            {TestPage && (
              <Route
                path="/test"
                element={
                  <Suspense fallback={null}>
                    <TestPage />
                  </Suspense>
                }
              />
            )}
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default ClientApp;
